import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const { requestLoginCode, verifyLoginCode } = useAuth();

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Por favor, insira seu email.');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      await requestLoginCode(email);
      setSuccessMessage('Código enviado para seu email!');
      setStep('code');
    } catch (error: any) {
      let errorMessage = 'Erro ao enviar código. Tente novamente.';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Tempo limite excedido. Verifique sua conexão e tente novamente.';
      } else if (error.response?.status === 408) {
        errorMessage = 'Serviço temporariamente indisponível. Tente novamente em alguns segundos.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code) {
      setError('Por favor, insira o código.');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      await verifyLoginCode(email, code);
      // Redirecionamento será feito automaticamente pelo AuthContext
    } catch (error: any) {
      setError(error.response?.data?.error || 'Código inválido. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode('');
    setError('');
    setSuccessMessage('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-24 w-24 flex items-center justify-center">
            <img 
              src="/logo-coffeein-white.png" 
              alt="CoffeeIn Logo" 
              className="h-40 w-40 object-contain"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Painel Administrativo
          </h2>
          <p className="mt-2 text-center text-sm text-gray-300">
            {step === 'email' 
              ? 'Digite seu email para receber o código de acesso'
              : 'Digite o código enviado para seu email'
            }
          </p>
        </div>

        <div className="bg-gray-900 py-8 px-6 shadow-2xl rounded-lg border border-gray-700">
          {step === 'email' ? (
            <form onSubmit={handleRequestCode} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-200">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-600 bg-gray-800 placeholder-gray-400 text-white rounded-lg focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 focus:z-10 sm:text-sm"
                  placeholder="seu@email.com"
                />
              </div>

              {error && (
                <div className="text-red-600 text-sm">{error}</div>
              )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                {isLoading ? (
                  <div className="spinner w-4 h-4"></div>
                ) : (
                  'Enviar Código'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-6">
              {successMessage && (
                <div className="text-emerald-400 text-sm bg-emerald-950/30 border border-emerald-800/50 p-3 rounded-lg">
                  {successMessage}
                </div>
              )}

              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-200">
                  Código de Verificação
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-600 bg-gray-800 placeholder-gray-400 text-white rounded-lg focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 focus:z-10 sm:text-sm text-center text-lg tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>

              {error && (
                <div className="text-red-600 text-sm">{error}</div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="flex-1 py-2 px-4 border border-gray-600 text-sm font-medium rounded-lg text-gray-200 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="spinner w-4 h-4"></div>
                  ) : (
                    'Entrar'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-400">
            Apenas usuários com permissões de administrador podem acessar este painel.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 